import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ChevronRight, ChevronDown, AlertTriangle, Search, X, Activity } from 'lucide-react'
import { bff } from '../services/bff'

// ---- data ----
const useMcpStats = (days) =>
  useQuery({
    queryKey: ['mcp', 'stats', days],
    queryFn: () => bff.get('/api/mcp/usage/stats', { params: { days: days || undefined } }).then((r) => r.data),
    staleTime: 15000,
    refetchInterval: 20000, // keep it live-ish while the panel is open
  })

const useMcpUsage = ({ tool, q, limit }) =>
  useQuery({
    queryKey: ['mcp', 'usage', tool, q, limit],
    queryFn: () =>
      bff.get('/api/mcp/usage', { params: { tool: tool || undefined, q: q || undefined, limit } })
        .then((r) => r.data.usage ?? []),
    staleTime: 8000,
    refetchInterval: 20000,
  })

// ---- helpers ----
const fmtTime = (ts) => {
  if (!ts) return '—'
  try {
    const d = new Date(ts)
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return ts }
}
const relDay = (ts) => (ts || '').slice(0, 10)

// output may be a JSON string, a clipped string, or an object — show it readable.
const pretty = (v) => {
  if (v == null) return ''
  if (typeof v === 'object') return JSON.stringify(v, null, 2)
  const s = String(v)
  try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s }
}

// ---- one expandable call record (the request/response drill-down) ----
function CallRow({ rec }) {
  const [open, setOpen] = useState(false)
  return (
    <li className={`rounded-lg border ${rec.is_error
      ? 'border-red-300 bg-red-50/50 dark:border-red-700/40 dark:bg-red-900/10'
      : 'border-parchment-border bg-white/60 dark:border-midnight-600 dark:bg-midnight-800/50'}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-ink-soft dark:text-mist/50" />
              : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-ink-soft dark:text-mist/50" />}
        <span className="font-mono text-xs text-ink dark:text-mist truncate">{rec.tool}</span>
        {rec.is_error && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-red-500/15 text-red-600 dark:text-red-300">
            <AlertTriangle className="w-3 h-3" /> error
          </span>
        )}
        <span className="ml-auto shrink-0 text-[11px] tabular-nums text-ink-soft dark:text-mist/50">{rec.ms}ms</span>
        <span className="shrink-0 text-[11px] text-ink-soft dark:text-mist/40 w-[130px] text-right">{fmtTime(rec.ts)}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-parchment-border/60 dark:border-midnight-600/60">
          {rec.request_id && (
            <div className="text-[11px] text-ink-soft dark:text-mist/40 font-mono">request_id: {rec.request_id}</div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gold-deep dark:text-gold mb-1">Request (input)</div>
            <pre className="text-[11px] leading-relaxed font-mono bg-parchment/60 dark:bg-midnight-900/60 rounded-md p-2.5 overflow-auto max-h-64 text-ink dark:text-mist/90">{pretty(rec.input) || '—'}</pre>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gold-deep dark:text-gold mb-1">Response (output)</div>
            <pre className={`text-[11px] leading-relaxed font-mono rounded-md p-2.5 overflow-auto max-h-80 ${rec.is_error
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200'
              : 'bg-parchment/60 dark:bg-midnight-900/60 text-ink dark:text-mist/90'}`}>{pretty(rec.output) || '—'}</pre>
          </div>
        </div>
      )}
    </li>
  )
}

export default function McpUsagePanel() {
  const [days, setDays] = useState('') // '' = all time
  const [tool, setTool] = useState(null) // active per-tool filter
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [limit, setLimit] = useState(100)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  const stats = useMcpStats(days)
  const usage = useMcpUsage({ tool, q: debounced, limit })
  const s = stats.data
  const maxDay = Math.max(1, ...((s?.by_day ?? []).map((d) => d.count)))

  return (
    <section className="rounded-xl border border-parchment-border bg-white/70 dark:border-midnight-600 dark:bg-midnight-800/60">
      <div className="p-5 border-b border-parchment-border/70 dark:border-midnight-600/70 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg text-ink dark:text-mist flex items-center gap-2">
            <Activity className="w-4 h-4 text-gold-deep dark:text-gold" /> MCP usage
          </h2>
          <p className="text-sm text-ink-soft dark:text-mist/60 mt-0.5">
            Every call through the Veralis MCP facade — which tools, how often, when, and the full request/response of each.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="shrink-0 h-9 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        >
          <option value="">all time</option>
          <option value="1">last 24h</option>
          <option value="7">last 7 days</option>
          <option value="30">last 30 days</option>
        </select>
      </div>

      <div className="p-5 space-y-5">
        {stats.isLoading && <div className="text-sm text-ink-soft dark:text-mist/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {stats.isError && <div className="text-sm text-red-600">{stats.error?.message}</div>}

        {s && s.total === 0 && (
          <div className="text-sm text-ink-soft dark:text-mist/50 italic">No MCP calls recorded yet in this window.</div>
        )}

        {s && s.total > 0 && (
          <>
            {/* Summary */}
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-2xl font-semibold text-gold-deep dark:text-gold">{s.total.toLocaleString()}</span>
                <span className="ml-2 text-ink-soft dark:text-mist/50">calls</span>
              </div>
              <div><span className="text-2xl font-semibold text-ink dark:text-mist">{s.distinct_tools}</span>
                <span className="ml-2 text-ink-soft dark:text-mist/50">tools</span></div>
              <div><span className={`text-2xl font-semibold ${s.errors ? 'text-red-600 dark:text-red-400' : 'text-ink dark:text-mist'}`}>{s.errors}</span>
                <span className="ml-2 text-ink-soft dark:text-mist/50">errors</span></div>
              <div className="text-xs text-ink-soft dark:text-mist/40 self-end">
                {fmtTime(s.first_ts)} → {fmtTime(s.last_ts)}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Per-tool: which + how many */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1.5">By tool — click to filter</div>
                <table className="w-full text-sm">
                  <tbody>
                    {s.tools.map((t) => (
                      <tr
                        key={t.tool}
                        onClick={() => setTool(tool === t.tool ? null : t.tool)}
                        className={`cursor-pointer border-b border-parchment-border/60 dark:border-midnight-600/60 ${tool === t.tool ? 'bg-gold/10' : 'hover:bg-gold/5'}`}
                      >
                        <td className="py-1 pr-2 font-mono text-xs truncate max-w-[160px]" title={t.tool}>{t.tool}</td>
                        <td className="py-1 text-right tabular-nums">{t.count}×</td>
                        <td className="py-1 pl-3 text-right text-xs">
                          {t.errors > 0
                            ? <span className="text-red-600 dark:text-red-400">{t.errors} err</span>
                            : <span className="text-ink-soft dark:text-mist/40">ok</span>}
                        </td>
                        <td className="py-1 pl-3 text-right text-xs tabular-nums text-ink-soft dark:text-mist/50">{t.avg_ms}ms</td>
                        <td className="py-1 pl-3 text-right text-[11px] text-ink-soft dark:text-mist/40">{relDay(t.last_ts)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Per-day: when */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50 mb-1.5">By day</div>
                <div className="space-y-1">
                  {s.by_day.map((d) => (
                    <div key={d.day} className="flex items-center gap-2 text-xs">
                      <span className="w-20 shrink-0 text-ink-soft dark:text-mist/50 tabular-nums">{d.day}</span>
                      <div className="flex-1 h-4 rounded bg-parchment/60 dark:bg-midnight-900/60 overflow-hidden">
                        <div className="h-full bg-gold/50 dark:bg-gold/40" style={{ width: `${(d.count / maxDay) * 100}%` }} />
                      </div>
                      <span className="w-10 text-right tabular-nums text-ink dark:text-mist">{d.count}</span>
                      {d.errors > 0 && <span className="w-12 text-right text-red-600 dark:text-red-400">{d.errors} err</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Detailed request/response log */}
        <div className="space-y-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[11px] uppercase tracking-wide text-ink-soft dark:text-mist/50">Calls</div>
            {tool && (
              <button onClick={() => setTool(null)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/15 text-gold-deep dark:text-gold text-xs">
                {tool} <X className="w-3 h-3" />
              </button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-soft dark:text-mist/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search inputs/outputs…"
                className="h-9 w-56 rounded-lg pl-8 pr-2 text-xs bg-white border border-parchment-border text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
              />
            </div>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-9 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
            >
              {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n} rows</option>)}
            </select>
          </div>

          {usage.isLoading && <div className="text-sm text-ink-soft dark:text-mist/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {usage.data && usage.data.length === 0 && (
            <div className="text-sm text-ink-soft dark:text-mist/50 italic">No matching calls.</div>
          )}
          {usage.data && usage.data.length > 0 && (
            <ul className="space-y-1.5">
              {usage.data.map((rec, i) => <CallRow key={`${rec.ts}-${i}`} rec={rec} />)}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}
